import React, { useEffect, useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import updateLocale from 'dayjs/plugin/updateLocale'
import { DateCalendar, LocalizationProvider, PickersDay, PickersDayProps } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Button, ClickAwayListener, Paper, Popper, Stack, Typography } from '@mui/material'
import { getEndOfWeek, getStartOfWeek } from '../../util/general'
import CalendarIcon from '../../assets/icons/CalendarIcon'
import ThinArrowRightIcon from '../../assets/icons/ThinArrowRightIcon'
import ThinArrowLeftIcon from '../../assets/icons/ThinArrowLeftIcon'
import { ExtraSmallButton, NonInteractiveTooltip } from '../custom'

dayjs.extend(isBetween)
dayjs.extend(updateLocale)

// ✅ Set Monday as the first day of the week globally
dayjs.updateLocale('en', { weekStart: 1 })

const WeekPickerDay = (props: PickersDayProps<Dayjs> & {
    hoveredDate: Dayjs | null;
    selectedWeek: { start: Dayjs; end: Dayjs } | null;
    setHoveredDate: (date: Dayjs | null) => void;
}) => {
    const { day, hoveredDate, selectedWeek, setHoveredDate, ...pickersDayProps } = props

    const startOfHoveredWeek = hoveredDate ? getStartOfWeek(hoveredDate) : null
    const endOfHoveredWeek = hoveredDate ? getEndOfWeek(hoveredDate) : null

    const isHoveredWeek = hoveredDate && day.isBetween(startOfHoveredWeek, endOfHoveredWeek, null, '[]')
    const isInHoveredWeek = hoveredDate && day.isBetween(startOfHoveredWeek, endOfHoveredWeek, null, '[]')

    const isInSelectedWeek = selectedWeek && day.isBetween(selectedWeek.start, selectedWeek.end, null, '[]')
    const isStartOfSelectedWeek = selectedWeek && day.isSame(selectedWeek.start, 'day')
    const isEndOfSelectedWeek = selectedWeek && day.isSame(selectedWeek.end, 'day')

    return (
        <PickersDay
            {...pickersDayProps}
            day={day}
            onMouseEnter={() => setHoveredDate(day)}
            onMouseLeave={() => setHoveredDate(null)}
            sx={{
                backgroundColor: isStartOfSelectedWeek || isEndOfSelectedWeek
                    ? 'rgba(0, 123, 255, 0.8)' // 🔵 Dark for selected start & end
                    : isInSelectedWeek
                        ? 'rgba(0, 123, 255, 0.3)' // 🔵 Light for selected middle days
                        : isInHoveredWeek
                            ? 'rgba(0, 123, 255, 0.2)' // 🔵 Light for hovered week (no dark start/end)
                            : 'transparent',
                color: isStartOfSelectedWeek || isEndOfSelectedWeek ? '#fff' : undefined,
                fontWeight: isStartOfSelectedWeek || isEndOfSelectedWeek ? 'bold' : 'normal'
            }}
        />
    )
}

interface WeekPickerProps {
    onChange: (startDate: Dayjs, endDate: Dayjs) => void;
    isCurrentWeek: boolean
}

const WeekPicker: React.FC<WeekPickerProps> = ({ onChange, isCurrentWeek }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [weekRange, setWeekRange] = useState<{ start: Dayjs; end: Dayjs } | null>(null)
    const [hoveredDate, setHoveredDate] = useState<Dayjs | null>(null)

    useEffect(() => {
        if (!weekRange || isCurrentWeek) {
            const today = dayjs()
            const start = dayjs(getStartOfWeek(today.toDate()))
            const end = dayjs(getEndOfWeek(today.toDate()))
            setWeekRange({ start, end })
        }
    }, [isCurrentWeek])

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(anchorEl ? null : event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const updateWeek = (date: Dayjs) => {
        const start = dayjs(getStartOfWeek(date.toDate()))
        const end = dayjs(getEndOfWeek(date.toDate()))
        setWeekRange({ start, end })
        onChange(start, end)
    }

    const handlePrevWeek = () => {
        if (weekRange) {
            const newStart = weekRange.start.subtract(7, 'day')
            updateWeek(newStart)
        }
    }

    const handleNextWeek = () => {
        if (weekRange) {
            const newStart = weekRange.start.add(7, 'day')
            updateWeek(newStart)
        }
    }

    const handleDateChange = (date: Dayjs | null) => {
        if (date) {
            const start = dayjs(getStartOfWeek(date.toDate()))
            const end = dayjs(getEndOfWeek(date.toDate()))
            setWeekRange({ start, end })
            onChange(start, end)
            handleClose()
        }
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack direction={'row'} alignItems={'center'} spacing={1} justifyContent={'center'}>
                <NonInteractiveTooltip title={'Select week'} arrow placement={'top'}>
                    <Button
                        variant="outlined"
                        color={'inherit'}
                        onClick={handleOpen}
                        sx={{ minWidth: '34px', p: 0, borderRadius: '4px', minHeight: '34px' }}
                    >
                        <CalendarIcon sx={{ width: '28px', height: '28px' }} />
                    </Button>
                </NonInteractiveTooltip>
                <NonInteractiveTooltip title={'Previous week'} arrow placement={'top'}>
                    <ExtraSmallButton sx={{ height: '34px', width: '34px' }} onClick={handlePrevWeek} color="inherit">
                        <ThinArrowLeftIcon sx={{ fontSize: '28px' }} />
                    </ExtraSmallButton>
                </NonInteractiveTooltip>
                <NonInteractiveTooltip title={'Next week'} arrow placement={'top'}>
                    <ExtraSmallButton sx={{ height: '34px', width: '34px' }} onClick={handleNextWeek} color="inherit">
                        <ThinArrowRightIcon sx={{ fontSize: '28px' }} />
                    </ExtraSmallButton>
                </NonInteractiveTooltip>
                {weekRange && (
                    <Typography variant="h6">
                        {weekRange.start.format('MMM D')} - {weekRange.end.format('MMM D, YYYY')}
                    </Typography>
                )}

            </Stack>
            <Popper open={Boolean(anchorEl)} anchorEl={anchorEl} placement="bottom-start" disablePortal
                sx={{ zIndex: 1300 }}>
                <ClickAwayListener onClickAway={handleClose}>
                    <Paper elevation={3} sx={{ p: 2 }}>
                        <DateCalendar<Dayjs>
                            onChange={handleDateChange}
                            showDaysOutsideCurrentMonth
                            referenceDate={weekRange?.start ?? dayjs()}
                            slots={{
                                day: (props) => (
                                    <WeekPickerDay
                                        {...props}
                                        hoveredDate={hoveredDate}
                                        setHoveredDate={setHoveredDate}
                                        selectedWeek={weekRange}
                                    />
                                )
                            }}
                        />
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </LocalizationProvider>
    );
}

export default WeekPicker
